
'use strict';

/**
 * Import type definitions allowing VS Code to show IntelliSense.
 *
 * @typedef {import('./CommonMethods').default} CommonMethods
 * @typedef {import('../antenne-frontend').Image} Image
 * @typedef {import('../openapi-generated').Url} Url
 * @typedef {import('../openapi-generated').Cover} ApiCover
 * @typedef {Omit<ApiCover, 'sizes'> & { sizes: ['200x200','300x300','600x600'] }} Cover
 * @typedef {import('../openapi-generated').Channel} ApiChannel
 * @typedef {Omit<ApiChannel, 'logo'> & { logo: Image[] }} Channel
 * @typedef {import('../antenne-api').Metadata} Metadata
 * @typedef {import('../openapi-streamingserver-generated').StreamMetaTitle} StreamingMetadataMessage
 * @typedef {StreamingMetadataMessage & {}} MetadataMessage
 * @typedef {{
 *   artist: string,
 *   title: string,
 *   cover: Url
 * }} MetadataFallback
 */

import ClassLogger from 'ClassLogger';

/**
 * This service class contains everything shared between the audioplayer.js and stadtlandfunkwebview.js
 */

export default class AudioPlayerService {
    /**
     * Returns the class name used by the ClassLogger.
     *
     * @returns {string}
     */
    getClassName () {
        return 'AudioPlayerService';
    }

    /**
     * @param {CommonMethods} commonMethods
     */
    constructor (CommonMethods) {
        this.commonMethods = CommonMethods;

        /** @protected @type {console} */
        this.logger = ClassLogger(this, true);
        /** @type {MetadataFallback} */
        this.fallbackMetadata = {};
    }

    /**
     * Returns a streaming metadata object derived from the given `metadata`.
     *
     * @param {Metadata} metadata
     *
     * @returns {StreamingMetadataMessage}
     */
    transformMetadataToStreamingMessage (metadata) {
        return {
            class: metadata.class,
            title: metadata.title,
            artist: metadata.artist,
            master_id: metadata.masterid,
            cover_url: this.createCoverUrl(metadata.cover),
            start_time_unix: Math.floor(new Date(metadata.starttime).getTime() / 1000),
        };
    }

    /**
     * Fill the fallback metadata object with details from the `channel` data.
     *
     * @param {Channel} channel
     *
     * @returns {this}
     */
    fillFallbackMetadata (channel = {}) {
        if (channel.title && channel.claim) {
            this.setPlayerUiFallbacks({
                title: channel.title,
                artist: channel.claim,
                cover: this.createCoverUrl(channel.logo),
            });
        }

        return this;
    }

    /**
     * Set the title, artist, and cover in the playbar.
     *
     * @param {Metadata} metadata
     *
     * @returns {this}
     */
    updateUi (metadata = {}) {
        if (!metadata.title) {
            metadata.title = this.fallbackMetadata.title;
        }

        if (!metadata.artist) {
            metadata.artist = this.fallbackMetadata.artist;
        }

        if (!metadata.cover) {
            metadata.cover = this.fallbackMetadata.cover;
        } else {
            metadata.cover = this.createCoverUrl(metadata.cover);
        }

        this.logger.info('Updating player UI', { metadata });

        return this
            .setTitleInPlayerUi(metadata.title)
            .setArtistInPlayerUi(metadata.artist)
            .setCoverImageInPlayerUi(metadata.cover);
    }

    /**
     * @returns {this}
     */
    setPlayerUiFallbacks ({ title, artist, cover } = {}) {
        this.fallbackMetadata.title = title;
        this.fallbackMetadata.artist = artist;
        this.fallbackMetadata.cover = cover;

        return this;
    }

    /**
     * Returns a URL for the given `coverImage`. The provided coverImage argument
     * can be of different types, depending on the source:
     *   - metadata API
     *   - metadata message from streaming server websocket
     *   - channel logos from channel configuration
     *
     * @param {string | Channel['logo'] | Cover} coverImage
     *
     * @returns {String}
     */
    createCoverUrl (coverImage) {
        if (!coverImage) {
            return this.fallbackMetadata.cover;
        }

        // this happens for websocket messages where cover images can be Base64 encoded or passed as the
        if (typeof coverImage === 'string') {
            return coverImage;
        }

        // type `Cover`: used by metadata items
        if (typeof coverImage === 'object' && !Array.isArray(coverImage)) {
            // translate `Cover` images to `Image` URLs

            if (coverImage.baseurl) {
                coverImage = coverImage.sizes.flatMap(size => {
                    return Object.values(coverImage.extensions).map(extension => {
                        return {
                            size,
                            url: `${coverImage.baseurl}${size}/${coverImage.filename}${extension}`,
                        };
                    });
                });
            }
        }

        // array of type `Image` (using `url` and `size`): used in channel configs
        if (Array.isArray(coverImage)) {
            const imageUrl =
            this.commonMethods.findMatchingImage(coverImage, '128x128', 'webp') ||
            this.commonMethods.findMatchingImage(coverImage, '128x128');

            if (imageUrl) {
                return imageUrl;
            }

            const svg = coverImage.find(image => String(image.url).endsWith('.svg'));

            if (svg) {
                return svg.url;
            }
        }

        return '';
    }

    /**
     * Set the given `title` in the audio player UI.
     *
     * @param {String} title
     *
     * @returns {this}
     */
    setTitleInPlayerUi (title) {
        const element = document.querySelector('[data-abywebplayer-currenttitle]');

        if (!element) {
            this.logger.warn('Cannot find element with "data-abywebplayer-currenttitle" attribute. Cannot set title');
        } else {
            element.textContent = title;
            element.ariaLabel = 'Name des Senders oder Podcasts: ' + title;
        }

        return this;
    }

    /**
     * Set the given `artist` in the audio player UI.
     *
     * @param {String} artist
     *
     * @returns {this}
     */
    setArtistInPlayerUi (artist) {
        const element = document.querySelector('[data-abywebplayer-currentartist]');

        if (!element) {
            this.logger.warn('Cannot find element with "data-abywebplayer-currentartist" attribute. Cannot set artist');
        } else {
            element.textContent = artist;
            element.ariaLabel = 'Künstler und Songtitel: ' + artist;
        }

        return this;
    }

    /**
     * Set the given `imageUrl` in the audio player UI for the cover image.
     *
     * @param {String} imageUrl
     *
     * @returns {this}
     */
    setCoverImageInPlayerUi (imageUrl) {
        if (!imageUrl) {
            this.logger.warn('Received invalid cover image URL. Ignoring it', { imageUrl });

            return this;
        }

        /** @type {HTMLImageElement | null} */
        const imgDiv = document.querySelector('[data-abywebplayer-image]');

        if (!imgDiv) {
            this.logger.warn('Cannot find element with "data-abywebplayer-image" attribute. Cannot set cover image');
        } else {
            /** We are creating dynamically an image el so that our HTML validates */
            const img = document.createElement('img');
            Array.from(imgDiv.attributes).forEach(attr => {
                img.setAttribute(attr.name, attr.value);
            });

            img.src = imageUrl;
            img.alt = '';
            imgDiv.replaceWith(img);

            /**
             * The onerror handler gets called when the provided `img.src` fails to load.
             * For example, this happens when song covers are not available. For such
             * cases we fallback to the channel cover. If we can’t load the channel
             * cover either, we’re defaulting to a transparent 1 pixel gif cover.
             */
            let hasImageLoadingFailed = false;
            img.onerror = () => {
                if (!hasImageLoadingFailed && this.mediaType === 'channel') {
                    this.logger.warn('Cover image is not available. Showing channel cover as fallback', {
                        imageUrl,
                        channelCoverUrl: this.fallbackMetadata.cover,
                    });

                    img.src = this.fallbackMetadata.cover;
                } else {
                    this.logger.warn('Channel cover is also not available. Showing empty placeholder');

                    // transparent 1x1 gif pixel
                    img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==';
                }

                hasImageLoadingFailed = true;
            };
        }

        return this;
    }
}
